import ValidationResult, { error, VALID } from "./ValidationResult";
import ActionValueValidator from "./ActionValueValidator";
import ActionValue from "../api/models/test-series/ActionValue";
import ParameterValue from "../api/models/test-series/ParameterValue";
import ParameterValueValidator from "./ParameterValueValidator";
import NumberParameterValue from "../api/models/test-series/NumberParameterValue";
import TestSeriesDTO from "../api/models/TestSeriesDTO";

function all(valid: ValidationResult[]): ValidationResult {
  return valid.reduce(
    (a, b) => ({
      valid: a.valid && b.valid,
      errors: a.errors.concat(b.errors),
    }),
    VALID,
  );
}

function noneEmpty(validationResult: ValidationResult[], message: string): ValidationResult[] {
  if (validationResult.length === 0) {
    return [error(message)];
  }
  return validationResult;
}

function allActionValues(actionValues: ActionValue[]): ValidationResult {
  return all(actionValues.map(ActionValueValidator.validate));
}

function allParameterValuesNoneEmpty(parameterValues: ParameterValue[]): ValidationResult {
  if (parameterValues.length === 0) {
    return error("Please add at least one parameter per condition.");
  }
  return all(parameterValues.map(ParameterValueValidator.validate));
}

function allUnitsNoneEmpty(parameterValues: ParameterValue[]): ValidationResult {
  if (parameterValues.length === 0) {
    return error("Please add at least one property.");
  }
  return all(
    parameterValues
      .filter((pv) => pv.type === "NUMBER")
      .map((pv) => {
        const valid = !!(pv as NumberParameterValue).unitCode;
        return valid ? VALID : error(`Please set a unit for "${pv.parameter.name}".`);
      }),
  );
}

export default class TestSeriesValidator {
  static validate(testSeries: TestSeriesDTO): ValidationResult {
    return all([
      all(
        testSeries.substrateSections.map((value) =>
          all([
            allActionValues(value.actionsOnAllSubstrates?.actionValues || []),
            ...noneEmpty(
              value.substrateActionFlows.map((x) =>
                x.actionFlow ? allActionValues(x.actionFlow.actionValues) : VALID,
              ),
              "Please add at least one substrate sample flow per column.",
            ),
          ]),
        ),
      ),
      all([
        allActionValues(testSeries.productSection.actionsOnAllProducts?.actionValues || []),
        ...noneEmpty(
          testSeries.productSection.productActionFlows.map((x) =>
            x.actionFlow ? allActionValues(x.actionFlow.actionValues) : VALID,
          ),
          "Please add at least one product sample flow.",
        ),
      ]),
      all([
        allActionValues(testSeries.specimenSection.buildingActionsOnAllSpecimen?.actionValues || []),
        ...testSeries.specimenSection.buildingActions.map((x) => allActionValues(x.actionValues)),
        allActionValues(testSeries.specimenSection.treatmentOnAllSpecimen?.actionValues || []),
        ...testSeries.specimenSection.treatments.map((x) => allActionValues(x.actionValues)),
      ]),
      all([
        allParameterValuesNoneEmpty(testSeries.conditionSection.parametersInAllTests),
        ...testSeries.conditionSection.conditions.map((x) => allParameterValuesNoneEmpty(x.parameterValues)),
      ]),
      all([allUnitsNoneEmpty(testSeries.measurementSection.measurements)]),
    ]);
  }
}
